// Header bell that polls the unread-count endpoint and opens a popover with
// the most recent notifications. Stops polling while the document is hidden.

import { Component, esc } from '../core/component.js';
import { api } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { showModal } from './Modal.js';

const POLL_MS = 5 * 60_000;

export class NotificationsBell extends Component {
  constructor() {
    super();
    this.state = { count: 0 };
    this._timer = null;
    this._onVisibility = this._onVisibility.bind(this);
    this._onFocus = this._onFocus.bind(this);
  }

  template() {
    return `
      <button class="btn btn-ghost btn-icon notif-bell" data-action="open" aria-label="${esc(t('notifications.title'))}">
        ${icon('bell', { size: 'sm' })}
        ${this.state.count > 0 ? `<span class="badge-count">${esc(String(this.state.count > 99 ? '99+' : this.state.count))}</span>` : ''}
      </button>
    `;
  }

  mount() {
    // this.el IS the button (template root), so attach directly.
    this.el.addEventListener('click', () => this._openModal());
    // mount() is called on every setState/_rerender; only start the poll loop
    // and the visibility listener once to avoid stacking intervals.
    if (auth.isAuthenticated && !this._timer && !document.hidden) this._poll();
    if (!this._visibilityBound) {
      document.addEventListener('visibilitychange', this._onVisibility);
      this._visibilityBound = true;
    }
  }

  unmount() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
    if (this._visibilityBound) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._visibilityBound = false;
    }
    if (this._focusBound) {
      window.removeEventListener('focus', this._onFocus);
      this._focusBound = false;
    }
  }

  _onFocus() {
    if (auth.isAuthenticated) this._refresh();
  }

  _onVisibility() {
    if (document.hidden) {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    } else if (auth.isAuthenticated && !this._timer) {
      this._poll();
    }
  }

  _poll() {
    this._refresh();
    this._timer = setInterval(() => this._refresh(), POLL_MS);
    // Refresh once when the tab regains focus so the user gets a fresh count
    // without having to wait for the next slow tick.
    if (!this._focusBound) {
      window.addEventListener('focus', this._onFocus);
      this._focusBound = true;
    }
  }

  async _refresh() {
    if (!auth.isAuthenticated) return;
    try {
      const res = await api.get('/notifications/unread-count');
      const next = Number(res.count) || 0;
      // Skip the re-render (and thus the remount) when nothing changed —
      // otherwise every poll triggers a needless DOM swap.
      if (next !== this.state.count) this.setState({ count: next });
    } catch (_) { /* ignore */ }
  }

  async _openModal() {
    const m = showModal({
      title: t('notifications.title'),
      body: `<div class="muted">${esc(t('notifications.loading'))}</div>`,
      actions: [
        { label: t('notifications.mark_all_read'), onClick: async () => {
          try { await api.post('/notifications/read-all'); } catch (_) {}
          this._refresh();
        }},
        { label: t('notifications.view_all'), onClick: () => { window.location.hash = '#/notifications'; } },
      ],
    });
    try {
      const res = await api.get('/notifications');
      const items = res.data || [];
      const slot = m.el.querySelector('[data-modal-body]');
      if (!slot) return;
      slot.innerHTML = items.length === 0
        ? `<div class="muted">${esc(t('notifications.empty'))}</div>`
        : `<div class="notif-list">${items.slice(0, 10).map((n) => this._row(n)).join('')}</div>`;
      slot.querySelectorAll('[data-mark]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          try { await api.post(`/notifications/${btn.getAttribute('data-mark')}/read`); btn.closest('.notif-row').classList.remove('unread'); } catch (_) {}
          this._refresh();
        });
      });
    } catch (_) { /* ignore */ }
  }

  _row(n) {
    const isUnread = !n.read_at;
    return `
      <div class="notif-row ${isUnread ? 'unread' : ''}">
        <div class="notif-title">${esc(n.title)}</div>
        <div class="muted">${esc(n.body || '')}</div>
        <div class="row-between">
          <span class="notif-time">${esc(n.created_at || '')}</span>
          ${isUnread ? `<button class="btn btn-ghost btn-sm" data-mark="${esc(n.id)}">${esc(t('common.close'))}</button>` : ''}
        </div>
      </div>
    `;
  }
}
